/** Centralized query key factory — keeps cache keys consistent and easy to invalidate */
export const queryKeys = {
  incidents: {
    all: ["incidents"] as const,
    list: (params?: {
      startDate?: string;
      endDate?: string;
      top?: number;
      search?: string;
      location?: string;
      programType?: string;
      status?: string;
    }) => ["incidents", "list", params] as const,
  },
  referrals: {
    all: ["referrals"] as const,
    list: (params?: {
      pageSize?: number;
      page?: number;
      location?: string;
      programType?: string;
    }) => ["referrals", "list", params] as const,
  },
  quickStats: {
    all: ["quickStats"] as const,
    // Single key since dashboard/stats returns all stats in one call
    stats: () => ["quickStats", "stats"] as const,
    // Keep for backwards compat
    count: (key: string) => ["quickStats", key] as const,
  },
  participants: {
    all: ["participants"] as const,
    list: (params?: {
      pageSize?: number;
      nextLink?: string;
      pinnedIds?: string;
      search?: string;
      programId?: string;
      location?: string;
      status?: string;
    }) => ["participants", "list", params] as const,
  },
  demographics: {
    all: ["demographics"] as const,
    detail: (personId: string) => ["demographics", personId] as const,
  },
  participantPrograms: {
    all: ["participantPrograms"] as const,
    list: (personId: string) => ["participantPrograms", personId] as const,
  },
  participantCases: {
    all: ["participantCases"] as const,
    list: (personId: string) => ["participantCases", personId] as const,
  },
  hearings: {
    all: ["hearings"] as const,
    list: (ppcId: string) => ["hearings", ppcId] as const,
  },
  auditLogs: {
    all: ["auditLogs"] as const,
    list: (ppcId: string) => ["auditLogs", ppcId] as const,
  },
  notes: {
    all: ["notes"] as const,
    list: (ppcId: string) => ["notes", ppcId] as const,
    runningCalendar: (ppcId: string) => ["notes", ppcId, "runningCalendar"] as const,
  },
  tasks: {
    all: ["tasks"] as const,
    list: (ppcId: string) => ["tasks", ppcId] as const,
  },
  myOpenTasks: {
    all: ["myOpenTasks"] as const,
    list: (params?: {
      top?: number;
      location?: string;
      programType?: string;
    }) => ["myOpenTasks", "list", params] as const,
  },
  documents: {
    all: ["documents"] as const,
    list: (ppcId: string) => ["documents", ppcId] as const,
  },
  calendar: {
    all: ["calendar"] as const,
    list: (hearingDate?: string, programType?: string, search?: string) =>
      ["calendar", hearingDate ?? "", programType ?? "", search ?? ""] as const,
    byParticipant: (participantId: string) =>
      ["calendar", "participant", participantId] as const,
  },
  programs: {
    all: ["programs"] as const,
    list: () => ["programs", "list"] as const,
    phases: (programId: string) => ["programs", programId, "phases"] as const,
    phasesByType: (programType: string) =>
      ["programs", "type", programType, "phases"] as const,
  },
  taskMaster: {
    all: ["taskMaster"] as const,
    list: (programId?: string) => ["taskMaster", "list", programId ?? ""] as const,
  },
  users: {
    all: ["users"] as const,
    list: (params?: { search?: string; isActive?: boolean; limit?: number; offset?: number }) =>
      ["users", "list", params] as const,
  },
  careTeam: {
    all: ["careTeam"] as const,
    list: (ppcId: string) => ["careTeam", ppcId] as const,
  },
  careTeamRoles: {
    all: ["careTeamRoles"] as const,
  },
  careTeamAll: {
    all: ["careTeamAll"] as const,
  },
  careTeamMembers: {
    all: ["careTeamMembers"] as const,
    list: (ppcId: string) => ["careTeamMembers", ppcId] as const,
  },
  progressCareTeamMembers: {
    all: ["progressCareTeamMembers"] as const,
    list: (ppcId: string) => ["progressCareTeamMembers", ppcId] as const,
  },
  managedCareTeamMembers: {
    all: ["managedCareTeamMembers"] as const,
    list: (params?: {
      search?: string;
      isActive?: boolean;
      role?: string;
      limit?: number;
      offset?: number;
    }) => ["managedCareTeamMembers", "list", params] as const,
    roles: () => ["managedCareTeamMembers", "roles"] as const,
  },
  listOfValues: {
    all: ["listOfValues"] as const,
    list: () => ["listOfValues", "list"] as const,
  },
  warrants: {
    all: ["warrants"] as const,
    list: (ppcId: string) => ["warrants", ppcId] as const,
  },
  ppc: {
    all: ["ppc"] as const,
    list: (participantId: string) => ["ppc", "list", participantId] as const,
    detail: (ppcId: string) => ["ppc", ppcId] as const,
  },
  aliases: {
    all: ["aliases"] as const,
    list: (participantId: string) =>
      ["aliases", "list", participantId] as const,
  },
  intake: {
    all: ["intake"] as const,
    detail: (participantId: string, programId: string) =>
      ["intake", participantId, programId] as const,
  },
  progress: {
    all: ["progress"] as const,
    detail: (ppcId: string) => ["progress", ppcId] as const,
  },
} as const;
